import { EiTableSortState } from '../models/ei-table.model'
import { EI_TABLE_PAGINATION } from '../constants/ei-table.constants'
import { ETableSortDirection } from '@app/core/enum/table.enum'

/**
 * Toggle sort state for a column.
 * Toggles between: asc -> desc -> asc (no "none" state)
 */
export function toggleSort(nextKey: string, current: EiTableSortState): EiTableSortState {
    if (current.activeKey !== nextKey) {
        // New column: start with ASC
        return { activeKey: nextKey, direction: ETableSortDirection.ASC }
    }
    // Same column: toggle between ASC and DESC
    if (current.direction === ETableSortDirection.ASC) {
        return { activeKey: nextKey, direction: ETableSortDirection.DESC }
    }
    // If DESC or empty, go back to ASC
    return { activeKey: nextKey, direction: ETableSortDirection.ASC }
}

/**
 * Safely get value from localStorage with fallback.
 * Returns fallback if key doesn't exist or parsing fails.
 */
export function safeGetLocalStorage<T>(key: string, fallback: T): T {
    try {
        const rawValue = localStorage.getItem(key)
        if (!rawValue) return fallback
        return JSON.parse(rawValue) as T
    } catch {
        return fallback
    }
}

/**
 * Safely set value in localStorage.
 * Silently fails if storage is unavailable or quota exceeded.
 */
export function safeSetLocalStorage<T>(key: string, value: T): void {
    try {
        localStorage.setItem(key, JSON.stringify(value))
    } catch {
        // Ignore storage errors (quota exceeded, disabled storage, etc.)
    }
}

/**
 * Build array of visible page numbers for pagination display.
 * Includes ellipsis markers when there are many pages.
 */
export function buildVisiblePages(currentPage: number, totalPages: number): number[] {
    const pages: number[] = []

    // Ensure valid inputs
    const safeCurrentPage = Math.max(1, Math.min(currentPage, totalPages))
    const safeTotalPages = Math.max(1, totalPages)

    // If total pages is small, show all pages
    if (safeTotalPages <= EI_TABLE_PAGINATION.maxVisiblePages) {
        for (let pageNumber = 1; pageNumber <= safeTotalPages; pageNumber++) {
            pages.push(pageNumber)
        }
        return pages
    }

    // Always show first page
    pages.push(1)

    // Calculate range around current page
    const rangeStart = Math.max(2, safeCurrentPage - EI_TABLE_PAGINATION.pageOffset)
    const rangeEnd = Math.min(safeTotalPages - 1, safeCurrentPage + EI_TABLE_PAGINATION.pageOffset)

    // Add ellipsis before range if needed
    if (rangeStart > 2) {
        pages.push(EI_TABLE_PAGINATION.ellipsisStart)
    }

    // Add pages in range
    for (let pageNumber = rangeStart; pageNumber <= rangeEnd; pageNumber++) {
        pages.push(pageNumber)
    }

    // Add ellipsis after range if needed
    if (rangeEnd < safeTotalPages - 1) {
        pages.push(EI_TABLE_PAGINATION.ellipsisEnd)
    }

    // Always show last page
    pages.push(safeTotalPages)

    return pages
}
